import { authHeaders } from '@/lib/authHeaders';

/** Self-service portal config: announcement banners + stage→column pinning. */
export interface PortalNotice {
  id: string;
  country: string; // '' = everyone
  text: string;
  tone: 'info' | 'warn';
  expiresAt: string | null;
  createdBy: string;
  createdAt: string;
}
export interface StageOverride { label: string; grp: string }
export interface RosterOverridePayload {
  workerId: string; hidden: boolean; name: string | null; role: string | null;
  country: string | null; paralegal: boolean | null; hubspotOwnerId: string | null;
  /** null = keep whatever department the code roster states. */
  department: string | null;
}
export interface AuditEntry { id: string; at: string; actor: string; action: string; detail: string; country: string }
export interface CopyOverridePayload { key: string; en: string | null; es: string | null; fr: string | null; pt: string | null }

const jsonHeaders = async () => ({ 'content-type': 'application/json', ...(await authHeaders()) });
async function asJson<T>(r: Response): Promise<T> {
  if (!r.ok) {
    let detail = '';
    try { detail = ((await r.json()) as { error?: string })?.error || ''; } catch { /* ignore */ }
    throw new Error(`${r.status} ${detail}`.trim());
  }
  return (await r.json()) as T;
}

export async function fetchPortalConfig(country: string, signal?: AbortSignal): Promise<{ notices: PortalNotice[]; stageMap: StageOverride[]; roster: RosterOverridePayload[]; copy: CopyOverridePayload[] }> {
  const r = await fetch(`/api/account/portal-config?country=${encodeURIComponent(country)}`, { headers: await authHeaders(), signal });
  const j = await asJson<{ notices?: PortalNotice[]; stageMap?: StageOverride[]; roster?: RosterOverridePayload[]; copy?: CopyOverridePayload[] }>(r);
  return { notices: j.notices || [], stageMap: j.stageMap || [], roster: j.roster || [], copy: j.copy || [] };
}

export interface ServerNotif {
  id: string; recipient: string; kind: string; important: boolean;
  title: string; sub: string; role: string | null; target: string; tab: string;
  ref_id: string; url: string; country: string; created_at: string;
}
/** The caller's server-generated notification feed (same on every device). */
export async function fetchMyNotifications(signal?: AbortSignal): Promise<ServerNotif[]> {
  const r = await fetch('/api/account/notifications', { headers: await authHeaders(), signal });
  return ((await asJson<{ items: ServerNotif[] }>(r)).items) || [];
}

/* ---- Admin (super only) ---- */
export async function adminPortalAll(signal?: AbortSignal): Promise<{ notices: PortalNotice[]; stageMap: StageOverride[]; audit: AuditEntry[]; roster: RosterOverridePayload[]; copy: CopyOverridePayload[] }> {
  const r = await fetch('/api/admin/portal', { headers: await authHeaders(), signal });
  return asJson(r);
}
export async function addNotice(input: { text: string; country?: string; tone?: 'info' | 'warn'; expiresAt?: string | null }): Promise<void> {
  const r = await fetch('/api/admin/portal/notice', { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(input) });
  await asJson<{ ok: boolean }>(r);
}
export async function deleteNotice(id: string): Promise<void> {
  const r = await fetch(`/api/admin/portal/notice?id=${encodeURIComponent(id)}`, { method: 'DELETE', headers: await authHeaders() });
  await asJson<{ ok: boolean }>(r);
}
export async function setRosterOverrideApi(input: {
  workerId: string; hidden?: boolean; name?: string; role?: string; country?: string;
  paralegal?: boolean; hubspotOwnerId?: string; department?: string; clear?: boolean;
}): Promise<void> {
  const r = await fetch('/api/admin/portal/roster', { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(input) });
  await asJson<{ ok: boolean }>(r);
}

/** Who came in and out, per country — the Activity page's numbers. */
export interface SessionPerson { email: string; country: string; logins: number; logouts: number; lastAt: string }
export interface SessionEvent { id: string; email: string; country: string; kind: 'in' | 'out'; at: string }
export interface SessionStats {
  days: number;
  byCountry: { country: string; logins: number; logouts: number; people: number }[];
  people: SessionPerson[];
  recent: SessionEvent[];
}

export async function fetchSessionStats(days: number, signal?: AbortSignal): Promise<SessionStats> {
  const r = await fetch(`/api/admin/portal/sessions?days=${days}`, { headers: await authHeaders(), signal });
  return asJson<SessionStats>(r);
}

export interface TrashedNote { id: string; country: string; text: string; author: string | null; ticketSubject: string | null; deletedAt: string | null }
/** A deleted task as the administrator's bin shows it — ANY owner's, which is
 *  what the per-person bin on the board deliberately does not do. */
export interface TrashedTask {
  id: string; country: string; title: string; owner: string | null;
  ticketSubject: string | null; deletedAt: string | null; status: string;
}
export async function fetchTrash(
  signal?: AbortSignal,
  opts: { country?: string; q?: string } = {},
): Promise<{
  links: import('./links').CustomLink[];
  notes: TrashedNote[];
  /** Contributed resources removed from the hub — restorable for 30 days. */
  resources?: import('./resources').ResourceItem[];
  /** Deleted tasks, every owner's. */
  tasks?: TrashedTask[];
  /** How many sit in the bin per country — the whole picture, unfiltered. */
  taskCounts?: { country: string; n: number }[];
}> {
  const qs = new URLSearchParams();
  if (opts.country) qs.set('country', opts.country);
  if (opts.q) qs.set('q', opts.q);
  const r = await fetch(`/api/admin/portal/trash${qs.toString() ? `?${qs}` : ''}`, { headers: await authHeaders(), signal });
  return asJson(r);
}
export async function restoreTrashed(kind: 'link' | 'note' | 'resource' | 'task', id: string, country?: string): Promise<void> {
  const r = await fetch('/api/admin/portal/trash/restore', { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify({ kind, id, country }) });
  await asJson<{ ok: boolean }>(r);
}

/** Empty the bin for good — irreversible, and only ever what is already in it.
 *  Tasks included: the bin lists them, so emptying it must reach them too. */
export async function purgeTrash(): Promise<{ links: number; notes: number; resources: number; tasks: number; total: number }> {
  const r = await fetch('/api/admin/portal/trash/purge', { method: 'POST', headers: await jsonHeaders() });
  return asJson<{ links: number; notes: number; resources: number; tasks: number; total: number }>(r);
}

export async function setCopyOverrideApi(input: { key: string; en?: string; es?: string; fr?: string; pt?: string; clear?: boolean }): Promise<void> {
  const r = await fetch('/api/admin/portal/copy', { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify(input) });
  await asJson<{ ok: boolean }>(r);
}

export async function setStagePin(label: string, grp: string, clear = false): Promise<void> {
  const r = await fetch('/api/admin/portal/stage', { method: 'POST', headers: await jsonHeaders(), body: JSON.stringify({ label, grp, clear }) });
  await asJson<{ ok: boolean }>(r);
}

/* ------------------------- Task templates -------------------------
 * The eighteen per-service lists, editable without a deploy. A template is a
 * LIVING link to the tasks it created: renaming a step renames them, removing
 * one sends them to the trash, and every call reports how many it touched so
 * the screen can say it out loud before and after.
 */
export interface TemplateStep {
  id: string; title: string;
  role: 'lawyer' | 'paralegal' | 'owner';
  kind: string; priority: 'high' | 'medium' | 'low';
  tiers?: string[]; alias?: string; source: string;
  position: number; enabled: boolean; builtin: boolean;
}
export interface ServiceTemplates { service: string; label: string; steps: TemplateStep[] }
/** How a case gets this playbook: the words its subject must carry, and the
 *  position of the rule in the chain (first match wins). */
export interface ServiceMatch { order: number; terms: string[]; and?: string[] }
export interface TemplatesPayload {
  items: ServiceTemplates[];
  /** service → the rules that select it, read off the matcher itself. */
  match?: Record<string, ServiceMatch[]>;
  /** Steps that are the SAME step in more than one service — renaming one is
   *  visibly not a local decision. */
  shared: { id: string; title: string; services: string[] }[];
  /** "<service>/<step>" → how many live tasks that step is responsible for. */
  usage: Record<string, number>;
}
export async function fetchTemplates(country: 'ES' | 'FR', signal?: AbortSignal): Promise<TemplatesPayload> {
  const r = await fetch(`/api/admin/portal/templates?country=${country}`, { headers: await authHeaders(), signal });
  return asJson<TemplatesPayload>(r);
}
export async function saveTemplateStep(
  country: 'ES' | 'FR', service: string, step: Partial<TemplateStep> & { id: string },
): Promise<{ renamed?: number }> {
  const r = await fetch('/api/admin/portal/templates', {
    method: 'POST', headers: await jsonHeaders(), body: JSON.stringify({ country, service, step }),
  });
  return asJson<{ ok: boolean; renamed?: number }>(r);
}
export async function reorderTemplate(country: 'ES' | 'FR', service: string, order: string[]): Promise<void> {
  const r = await fetch('/api/admin/portal/templates', {
    method: 'POST', headers: await jsonHeaders(), body: JSON.stringify({ country, service, order }),
  });
  await asJson<{ ok: boolean }>(r);
}
export async function removeTemplateStep(
  country: 'ES' | 'FR', service: string, step: string, withTasks = false,
): Promise<{ disabled: boolean; tasksRemoved: number }> {
  const q = new URLSearchParams({ country, service, step, ...(withTasks ? { withTasks: '1' } : {}) });
  const r = await fetch(`/api/admin/portal/templates?${q}`, { method: 'DELETE', headers: await jsonHeaders() });
  return asJson<{ ok: boolean; disabled: boolean; tasksRemoved: number }>(r);
}
export async function resetTemplates(country: 'ES' | 'FR'): Promise<{ restored: number }> {
  const r = await fetch('/api/admin/portal/templates', {
    method: 'POST', headers: await jsonHeaders(), body: JSON.stringify({ country, reset: true }),
  });
  return asJson<{ ok: boolean; restored: number }>(r);
}
